import fp from 'lodash/fp';
import React from 'react';
import { Breadcrumb, BreadcrumbItem, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { Link } from 'react-router-dom';
import { branch, compose, lifecycle, renderComponent, withProps } from 'recompose';
import { bindActionCreators } from 'redux';
import { routes } from '../../../../constants';
import { isBusinessAdmin } from '../../../../services';
import { cpdCategorysActions } from '../../../../state/modules/areas-of-advice';
import { businessActions, businessSelector, fullTeamCpdCategorysSelector, fullTeamPendingExternalCpdSelector, invitationsSelector, teamMembersSelector, userCpdDaysRemaining } from '../../../../state/modules/business';
import { publisherActions } from '../../../../state/modules/publisher';
import { currentUserSelector } from '../../../../state/modules/user';
import { noCapMap, pluralise } from '../../../../utils';
import { NotificationMessage } from '../../../components';
import { ProgressChart } from '../../../partials';
import Loading from '../../loading';
import { InvitationRow, TeamMemberRow } from './partials';

const CommonTeamMembersHeader = ({ title, displayProgress }) => (
  <section id='header--teams' key='team-header' className='layout--header'>
    <Grid>
      <Row className='button-gutter'>
        <Col xs={12} md={displayProgress ? 5 : 12}>
          <Breadcrumb>
            <LinkContainer to={routes.app.teams.root}>
              <BreadcrumbItem>
                Teams
              </BreadcrumbItem>
            </LinkContainer>
            <BreadcrumbItem active>
              {title}
            </BreadcrumbItem>
          </Breadcrumb>
          <h1>
            <i className='icon-team-large' /> {title}
          </h1>
          <hr />
        </Col>
      </Row>
    </Grid>
  </section>
);

const CommonTeamMembersDetails = branch(
  ({ teamCpdCategorys }) => !teamCpdCategorys,
  renderComponent(Loading)
)(({
  title,
  displayProgress,
  teamMembers,
  teamAdministrators,
  teamSize,
  teamCpdCategorys,
  uniqueTeamPendingExternalCpd,
  cpdCategorys,
  invitations,
  progressPercentage,
  userIndustries,
  hideDaysRemaining,
  hidePointsFraction
}) => (
    <section id='section--teams' className='layout--section'>
      <div className='container'>
        {(uniqueTeamPendingExternalCpd && uniqueTeamPendingExternalCpd.length > 0) ?
          <NotificationMessage message={
            <span key={0}>
              {uniqueTeamPendingExternalCpd.length === 1 ?
                `${uniqueTeamPendingExternalCpd.length} pending External CPD activity is waiting for review. Review it ` :
                `${uniqueTeamPendingExternalCpd.length} pending External CPD activities are waiting for review. Review them `
              }< Link className='btn-plain' key={1} to={routes.app.externalCpd.review}>here.</Link>
            </span>
          } /> : null
        }
        <Row className='no-gutter tableHeader'>
          {
            displayProgress ?
              <Col xs={12} md={1}>
                <p>Progress</p>
              </Col> : null
          }
          <Col xs={12} md={6}>
            <p>Team details</p>
          </Col>
        </Row>
        <div className='teamListing titleTeam'>
          <h2 className='hide-md'>{title}</h2>
          <Row className='sm-gutter infoRow'>
            {
              displayProgress ? (<Col xs={3} md={1}>
                <div className='chart'>
                  <ProgressChart progressPercentage={progressPercentage} width={100} height={100} />
                </div>
              </Col>) : null
            }
            <Col xs={6} md={6}>
              <h2 className='hide-sm'>{title}</h2>
              <p>
                <strong>
                  <small>
                    {teamSize} {pluralise('member', teamSize)}
                  </small>
                </strong>
              </p>
              <p>
                <small>
                  {invitations.length || 'No'} pending {pluralise('invitation', invitations.length)}
                </small>
              </p>
            </Col>
          </Row>
        </div>
        {
          noCapMap((teamMemberList, index) =>
            noCapMap((teamMember, index) =>
              <TeamMemberRow
                key={`member-${index}`}
                displayProgress={displayProgress}
                teamMember={teamMember}
                cpdCategorys={cpdCategorys}
                hidePointsFraction={hidePointsFraction}
                hideDaysRemaining={hideDaysRemaining}
                cpdCategorysStats={fp.find(
                  ({ userId }) => userId === teamMember.user.id
                )(teamCpdCategorys)}
                userCpdDaysRemaining={userCpdDaysRemaining(
                  fp.flow(
                    fp.get(teamMember.user.id),
                    fp.head
                  )(userIndustries)
                )} />
            )(fp.sortBy(({
              user: {
                firstName,
                lastName
              }
            }) => fp.lowerCase(`${firstName} ${lastName}`))(teamMemberList))
          )([teamAdministrators, teamMembers])
        }
        {
          noCapMap((invitation, index) =>
            <InvitationRow key={`invitaion-${index}`} invitation={invitation} />
          )(invitations)
        }
      </div>
    </section>
  ));

const CommonTeamMembersPage = (props) => (
  <div className='base-layout'>
    <CommonTeamMembersHeader key='team-header' {...props} />
    <CommonTeamMembersDetails key='team-details' {...props} />
  </div>
);

const mapStateToProps = (state) => ({
  user: currentUserSelector(state),
  business: businessSelector(state),
  teamAdministrators: fp.filter(isBusinessAdmin)(teamMembersSelector(state)),
  teamMembers: fp.filter(fp.negate(isBusinessAdmin))(teamMembersSelector(state)),
  teamCpdCategorys: fullTeamCpdCategorysSelector(state),
  teamPendingExternalCpd: fullTeamPendingExternalCpdSelector(state),
  isFetchingTeamCpdCategorys: state.business.isFetchingTeamCpdCategorys,
  isFetchingPendingExternalCpd: state.business.isFetchingPendingExternalCpd,
  cpdCategorys: state.cpdCategorys.cpdCategorys,
  invitations: fp.filter(({ status }) => status !== 'Accepted')(invitationsSelector(state)),
  userIndustries: state.business.userIndustries
});

const mapDispatchToProps = (dispatch) => ({
  getIndustryRoles: bindActionCreators(businessActions.business.user.getIndustryRoles, dispatch),
  getFullTeamCpdCategorys: bindActionCreators(businessActions.business.getFullTeamCpdCategorys, dispatch),
  getFullTeamPendingExternalCpd: bindActionCreators(businessActions.business.getFullTeamPendingExternalCpd, dispatch),
  getCpdCategorys: bindActionCreators(cpdCategorysActions.cpdCategorys.get, dispatch),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      const {
        cpdCategorys,
        business,
        isFetchingCpdCategorys,
        isFetchingTeamCpdCategorys,
        isFetchingPendingExternalCpd,
        getCpdCategorys,
        getFullTeamCpdCategorys,
        getFullTeamPendingExternalCpd,
        getIndustryRoles,
        teamAdministrators,
        teamMembers,
        getPublishers
      } = this.props

      if (fp.isEmpty(cpdCategorys) && !isFetchingCpdCategorys) {
        getCpdCategorys();
      }
      if (business && !isFetchingTeamCpdCategorys) {
        getFullTeamCpdCategorys(business.id);
      }
      if (business && !isFetchingPendingExternalCpd) {
        getFullTeamPendingExternalCpd(business.id);
      }
      getPublishers()
      fp.concat(teamAdministrators)(teamMembers).map((teamMember) => getIndustryRoles(business.id, teamMember.user.id));
    },
    componentWillReceiveProps(nextProps) {
      const {
        business,
        teamCpdCategorys,
        teamPendingExternalCpd,
        teamMembers,
        teamAdministrators,
        isFetchingTeamCpdCategorys,
        isFetchingPendingExternalCpd,
        getIndustryRoles,
        getFullTeamCpdCategorys,
        getFullTeamPendingExternalCpd

      } = this.props

      const {
        business: nextBusiness,
        teamMembers: nextTeamMembers,
        teamAdministrators: nextTeamAdministrators
      } = nextProps

      if (((!business && nextBusiness) || (!teamCpdCategorys && nextBusiness)) && !isFetchingTeamCpdCategorys) {
        getFullTeamCpdCategorys(nextBusiness.id);
      }
      if (((!business && nextBusiness) || (!teamPendingExternalCpd && nextBusiness)) && !isFetchingPendingExternalCpd) {
        getFullTeamPendingExternalCpd(nextBusiness.id);
      }
      if (!fp.isEqual(teamAdministrators)(nextTeamAdministrators)) {
        nextTeamAdministrators.map((teamAdministrators) => getIndustryRoles(business.id, teamAdministrators.user.id));
      }
      if (!fp.isEqual(teamMembers)(nextTeamMembers)) {
        nextTeamMembers.map((teamMember) => getIndustryRoles(business.id, teamMember.user.id));
      }
    }
  }),
  withProps(({ teamPendingExternalCpd }) => ({
    uniqueTeamPendingExternalCpd: fp.uniqBy('externalCpdId')(teamPendingExternalCpd)
  })),
  withProps(({ teamCpdCategorys }) => ({
    progressPercentage: fp.flow(
      fp.map(
        fp.get('derivedStat.progress')
      ),
      fp.sum,
      (totalPercentage) => totalPercentage / fp.max([fp.get('length')(teamCpdCategorys), 1])
    )(teamCpdCategorys)
  })),
  withProps(({ teamMembers, teamAdministrators, teamMemberFilter }) => ({
    teamMembers: fp.filter(teamMemberFilter)(teamMembers),
    teamAdministrators: fp.filter(teamMemberFilter)(teamAdministrators)
  })),
  withProps(({ teamMembers, teamAdministrators }) => ({
    teamSize: teamMembers.length + teamAdministrators.length
  }))
)(CommonTeamMembersPage)



// WEBPACK FOOTER //
// ./src/containers/pages/app/teams/common-team-members.js